import { useMemo } from 'react'
import {
  CurrentYearDualCharts,
  type DualChartPoint
} from '../../components/charts/CurrentYearDualCharts'
import type {
  MonthlyBucket,
  QuarterlyBucket
} from '../../../viewmodels/useCurrentYearVariantPricingHistory'

interface Props {
  readonly year: number | null
  readonly monthly: ReadonlyArray<MonthlyBucket>
  readonly quarterly: ReadonlyArray<QuarterlyBucket>
  readonly isLoading: boolean
  readonly error: string | null
  readonly onRetry: () => void
}

const COST_COLOR = '#7A6E5F'
const SELLING_COLOR = '#A6926B'

const MONTH_LABELS = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const

/**
 * Current-year baseline-cost vs selling-price chart pair. A thin adapter
 * over the shared CurrentYearDualCharts — maps the pricing buckets
 * (paisa → ₹) onto the generic two-series shape. Cost is series A
 * (live per-variant value, near-static); selling price is series B.
 */
export function CurrentYearVariantPricingHistoryCharts({
  year,
  monthly,
  quarterly,
  isLoading,
  error,
  onRetry
}: Props): JSX.Element {
  const monthlyData = useMemo<DualChartPoint[]>(
    () =>
      monthly.map((m) => ({
        label: MONTH_LABELS[m.month] ?? String(m.month),
        a: m.avg_baseline_cost_paisa / 100,
        b: m.avg_selling_price_paisa / 100
      })),
    [monthly]
  )
  const quarterlyData = useMemo<DualChartPoint[]>(
    () =>
      quarterly.map((q) => ({
        label: `Q${q.quarter}`,
        a: q.avg_baseline_cost_paisa / 100,
        b: q.avg_selling_price_paisa / 100
      })),
    [quarterly]
  )

  return (
    <CurrentYearDualCharts
      year={year}
      monthly={monthlyData}
      quarterly={quarterlyData}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
      seriesA={{ name: 'Baseline cost (avg)', color: COST_COLOR }}
      seriesB={{ name: 'Selling price (avg)', color: SELLING_COLOR }}
      aggregateLabel="average"
      formatValue={(n) => `₹${n.toFixed(2)}`}
      formatAxis={(n) => `₹${formatCompact(n)}`}
      emptyNote="No snapshots in this year yet."
    />
  )
}

function formatCompact(n: number): string {
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return String(Math.round(n))
}
